import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { register, login } from '../services/api';
import { useToast } from '../hooks/useToast';

function BrandLogo() {
  return (
    <span className="auth-brand-logo" aria-hidden>
      <span className="auth-brand-logo__surface">
        <span className="auth-brand-logo__shape auth-brand-logo__shape--a" />
        <span className="auth-brand-logo__shape auth-brand-logo__shape--b" />
        <span className="auth-brand-logo__shape auth-brand-logo__shape--c" />
      </span>
    </span>
  );
}

export { BrandLogo };

export default function AuthPage({ onAuth }) {
  const toast = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        setTimeout(() => toast.success('Welcome back!'), 300);
      } else {
        await register(email, password);
        setTimeout(() => toast.success('Account created! Welcome to DS CodeXAI.'), 300);
      }
      onAuth();
    } catch (err) {
      setError(err.message);
      toast.error(isLogin ? 'Sign in failed: ' + err.message : 'Sign up failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <style>{AUTH_CSS}</style>

      {/* ── Back link — fixed top-left ── */}
      <Link to="/" className="auth-back-link">← Back to home</Link>

      {/* ── Centered content ── */}
      <div className="auth-center">
        {/* Brand above card */}
        <div className="auth-identity">
          <BrandLogo />
          <span className="auth-identity__name">DS CodeXAI</span>
          <span className="auth-identity__tag">Autonomous Software Engineering Platform</span>
        </div>

        {/* Glass card */}
        <div className="auth-card">
          <div className="auth-card__header">
            <h2 className="auth-card__title">Welcome back</h2>
            <p className="auth-card__sub">Continue building with DS CodeXAI.</p>
          </div>

          {/* Tabs */}
          <div className="auth-tabs">
            <button
              type="button"
              className={`auth-tab${isLogin ? ' auth-tab--active' : ''}`}
              onClick={() => { setIsLogin(true); setError(''); }}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`auth-tab${!isLogin ? ' auth-tab--active' : ''}`}
              onClick={() => { setIsLogin(false); setError(''); }}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="auth-input"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="auth-input"
              />
              {isLogin && (
                <Link to="/forgot-password" className="auth-forgot-link">Forgot Password?</Link>
              )}
            </div>

            {error && (
              <div className="auth-error">
                <span className="auth-error__icon">⚠</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`auth-btn${loading ? ' auth-btn--loading' : ''}`}
            >
              {loading ? 'Please wait…' : isLogin ? 'Continue' : 'Create Account'}
            </button>
          </form>

          <p className="auth-switch">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <span
              className="auth-switch__link"
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
            >
              {isLogin ? 'Create one' : 'Sign in'}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export const AUTH_CSS = `
/* ─── Root ─── */
.auth-root {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(ellipse 80% 55% at 15% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 65%),
    radial-gradient(ellipse 60% 45% at 85% 100%, rgba(139, 92, 246, 0.07) 0%, transparent 65%),
    var(--bg-primary);
  color: var(--text-primary);
  overflow: hidden;
  position: relative;
}
[data-theme="light"] .auth-root {
  background:
    radial-gradient(ellipse 80% 55% at 15% 0%, rgba(99, 102, 241, 0.05) 0%, transparent 65%),
    radial-gradient(ellipse 60% 45% at 85% 100%, rgba(139, 92, 246, 0.04) 0%, transparent 65%),
    #fafbff;
}

/* ─── Centered layout ─── */
.auth-center {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
  padding: 40px 20px 60px;
  gap: 0;
}

/* Back link — fixed top-left corner */
.auth-back-link {
  position: fixed;
  top: 20px;
  left: 24px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: color 0.2s ease, opacity 0.2s ease;
  opacity: 0.7;
}
.auth-back-link:hover { opacity: 1; color: var(--accent); }

/* ─── Brand identity above card ─── */
.auth-identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  text-align: center;
}
.auth-identity__name {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: var(--text-primary);
  line-height: 1;
}
.auth-identity__tag {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-muted);
  letter-spacing: 0.02em;
}

/* ─── Logo mark ─── */
.auth-brand-logo {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
}
.auth-brand-logo__surface {
  position: relative;
  display: block;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(99, 102, 241, 0.30);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 4px 20px -6px rgba(99, 102, 241, 0.40);
  overflow: hidden;
}
[data-theme="light"] .auth-brand-logo__surface {
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(99, 102, 241, 0.20);
}
.auth-brand-logo__shape {
  position: absolute;
  border-radius: 3px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}
.auth-brand-logo__shape--a { width: 14px; height: 14px; top: 9px; left: 9px; opacity: 0.95; }
.auth-brand-logo__shape--b {
  width: 10px; height: 10px; top: 10px; right: 9px; opacity: 0.65;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(99, 102, 241, 0.5));
}
.auth-brand-logo__shape--c { width: 18px; height: 5px; bottom: 9px; left: 9px; border-radius: 2px; opacity: 0.80; }

/* ─── Glass card ─── */
.auth-card {
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 36px 32px 28px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow:
    0 8px 48px rgba(0, 0, 0, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 0 0 1px rgba(99, 102, 241, 0.06);
}
[data-theme="light"] .auth-card {
  background: rgba(255, 255, 255, 0.88);
  border-color: rgba(99, 102, 241, 0.10);
  box-shadow:
    0 4px 40px rgba(99, 102, 241, 0.09),
    0 1px 3px rgba(15, 23, 42, 0.05);
}

.auth-card__header { margin-bottom: 24px; }
.auth-card__title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--text-primary);
  margin: 0 0 6px;
}
.auth-card__sub {
  font-size: 13px;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
}

/* Tabs */
.auth-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  margin-bottom: 24px;
}
[data-theme="light"] .auth-tabs {
  background: rgba(99, 102, 241, 0.04);
  border-color: rgba(99, 102, 241, 0.10);
}
.auth-tab {
  padding: 9px 0;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--text-muted);
  transition: all 0.2s ease;
}
.auth-tab--active {
  background: rgba(99, 102, 241, 0.15);
  color: var(--text-primary);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
}
[data-theme="light"] .auth-tab--active {
  background: #fff;
  color: var(--accent);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.10);
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.auth-field { display: flex; flex-direction: column; gap: 6px; }
.auth-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.auth-input {
  width: 100%;
  padding: 11px 14px;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}
[data-theme="light"] .auth-input {
  background: #fff;
  border-color: rgba(99, 102, 241, 0.14);
}
.auth-input:focus {
  border-color: rgba(99, 102, 241, 0.50);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.10);
}
.auth-input::placeholder { color: var(--text-muted); opacity: 0.6; }

.auth-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.22);
  color: #f87171;
  font-size: 13px;
  font-weight: 500;
}
[data-theme="light"] .auth-error { color: #dc2626; }
.auth-error__icon { flex-shrink: 0; }

.auth-btn {
  width: 100%;
  padding: 13px 0;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  letter-spacing: 0.01em;
  margin-top: 4px;
}
.auth-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
}
.auth-btn:active:not(:disabled) { transform: scale(0.99); }
.auth-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.auth-btn--loading {
  opacity: 0.65;
  cursor: not-allowed;
  background: rgba(99, 102, 241, 0.35);
  box-shadow: none;
  transform: none;
}

.auth-switch {
  text-align: center;
  margin-top: 20px;
  font-size: 13px;
  color: var(--text-muted);
}
.auth-switch__link {
  color: var(--accent);
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.auth-switch__link:hover { opacity: 0.8; }

/* ─── Forgot password link ─── */
.auth-forgot-link {
  align-self: flex-end;
  font-size: 12px;
  font-weight: 500;
  color: var(--accent, #6366f1);
  text-decoration: none;
  margin-top: 4px;
  transition: opacity 0.2s ease;
}
.auth-forgot-link:hover { opacity: 0.7; }

/* ─── Shared success state ─── */
.auth-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  animation: authFadeUp 0.4s ease;
}
.auth-success__icon { margin-bottom: 4px; }
.auth-success__msg {
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
  line-height: 1.6;
  max-width: 300px;
}
.auth-success__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  margin-top: 8px;
}

/* ─── Password field extras ─── */
.auth-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.auth-input-wrap .auth-input { padding-right: 42px; }
.auth-eye-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
}
.auth-eye-btn:hover { opacity: 1; }

.auth-strength {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}
.auth-strength__bar {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: rgba(255,255,255,0.08);
  transition: background 0.3s ease;
}
[data-theme="light"] .auth-strength__bar { background: rgba(0,0,0,0.06); }
.auth-strength__bar--filled-1 { background: #ef4444; }
.auth-strength__bar--filled-2 { background: #f59e0b; }
.auth-strength__bar--filled-3 { background: #eab308; }
.auth-strength__bar--filled-4 { background: #22c55e; }
.auth-strength__bar--filled-5 { background: #10b981; }

.auth-requirements {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.auth-requirements li {
  font-size: 11px;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s ease;
}
.auth-requirements li.met { color: #22c55e; }
.auth-req-icon {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.12);
  transition: all 0.2s ease;
}
[data-theme="light"] .auth-req-icon { border-color: rgba(0,0,0,0.1); }
.auth-requirements li.met .auth-req-icon {
  background: rgba(34, 197, 94, 0.15);
  border-color: #22c55e;
  color: #22c55e;
}

.auth-field-error {
  font-size: 12px;
  color: #f87171;
  margin-top: 2px;
}
[data-theme="light"] .auth-field-error { color: #dc2626; }

@keyframes authFadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ─── Responsive ─── */
@media (max-width: 480px) {
  .auth-center { padding: 32px 16px 48px; }
  .auth-card { padding: 28px 20px 24px; }
  .auth-back-link { top: 16px; left: 16px; }
  .auth-identity { margin-bottom: 22px; }
}
`;
